/**
 * Panel Tabs — dependency-free tabs primitive (D1 decision).
 *
 * Replaces the vendored radix/shadcn tabs for the shell's docked panel
 * groups. The API mirrors the radix surface the shell uses: a controlled
 * root (`value`/`onValueChange`/`defaultValue`), a list, triggers carrying
 * `data-state="active|inactive"` for styling, and content panes that mount
 * only when active. Everything renders in the light DOM with plain elements
 * so no app needs radix to compile the shell.
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface TabsContextValue {
    value: string;
    setValue: (next: string) => void;
    ids: React.MutableRefObject<Set<string>>;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
    const ctx = useContext(TabsContext);
    if (!ctx) {
        throw new Error('Panel tabs must be used within <Tabs>');
    }
    return ctx;
};

export interface TabsProps {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    className?: string;
    children?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({
    value: controlledValue,
    defaultValue,
    onValueChange,
    className,
    children,
}) => {
    const [uncontrolledValue, setUncontrolledValue] = useState<string>(
        defaultValue ?? '',
    );
    const value = controlledValue !== undefined ? controlledValue : uncontrolledValue;
    const ids = useRef<Set<string>>(new Set());

    const setValue = (next: string) => {
        if (controlledValue === undefined) setUncontrolledValue(next);
        onValueChange?.(next);
    };

    return (
        <TabsContext.Provider value={{ value, setValue, ids }}>
            <div className={className} data-state={value ? 'active' : undefined}>
                {children}
            </div>
        </TabsContext.Provider>
    );
};

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
}

export const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
    ({ className, ...props }, ref) => (
        <div ref={ref} role="tablist" className={className} {...props} />
    ),
);
TabsList.displayName = 'TabsList';

export interface TabsTriggerProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    value: string;
}

export const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
    ({ value, className, onClick, ...props }, ref) => {
        const ctx = useTabsContext();
        useEffect(() => {
            ctx.ids.current.add(value);
            return () => {
                ctx.ids.current.delete(value);
            };
        }, [ctx, value]);

        const active = ctx.value === value;
        return (
            <button
                ref={ref}
                type="button"
                role="tab"
                aria-selected={active}
                data-state={active ? 'active' : 'inactive'}
                className={className}
                onClick={(e) => {
                    onClick?.(e);
                    if (!e.defaultPrevented) ctx.setValue(value);
                }}
                {...props}
            />
        );
    },
);
TabsTrigger.displayName = 'TabsTrigger';

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
    value: string;
    /** Kept for API parity with the radix version; the content always
        unmounts when inactive, which is what the shell relied on anyway. */
    forceMount?: true;
}

export const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
    ({ value, forceMount: _forceMount, className, children, ...props }, ref) => {
        const ctx = useTabsContext();
        const active = ctx.value === value;
        if (!active) return null;
        return (
            <div
                ref={ref}
                role="tabpanel"
                data-state="active"
                className={className}
                {...props}
            >
                {children}
            </div>
        );
    },
);
TabsContent.displayName = 'TabsContent';
